import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { sharedAgentMessages } from '../../shared.messages';

/**
 * Right-aligned **Cancel** + **Approve** row shared by every inline
 * HITL approval surface. Cancel still maps to ``verdict: 'deny'`` on
 * the wire — the audit trail stays honest, only the user-facing label
 * is softer. While the resolve is in flight the matching button shows
 * an inline spinner via ``pendingVerdict``.
 */
export interface InlineApprovalActionsProps {
  /** Click handler for **Approve** (verdict='approve' on the wire). */
  onApprove: () => void | Promise<void>;
  /** Click handler for **Cancel** (verdict='deny' on the wire). */
  onDeny: () => void | Promise<void>;
  /**
   * Which verdict was most recently posted — drives the inline spinner
   * and locks the row from re-submission. ``null`` means idle.
   */
  pendingVerdict: 'approve' | 'deny' | null;
  /**
   * Soft-disable the whole row (e.g. while the parent's mutation is
   * still pending after ``pendingVerdict`` cleared). Spinner stays on
   * the previously-clicked button if ``pendingVerdict`` is also set.
   */
  disabled?: boolean;
  /** Disable only Approve while keeping Cancel available. */
  approveDisabled?: boolean;
  /** Override the default "Approve" label on the primary button. */
  approveLabel?: string;
  /** Override the default "Cancel" label on the deny button. */
  denyLabel?: string;
  /** Override the Approve button test id. */
  approveTestId?: string;
  /** Override the deny button test id. */
  denyTestId?: string;
  /** Extra classes on the action row (e.g. compose footers that left-align Send). */
  className?: string;
  /** A secondary action left of Cancel, such as showing the card's details. */
  leading?: ReactNode;
}

export const InlineApprovalActions = ({
  onApprove,
  onDeny,
  pendingVerdict,
  disabled = false,
  approveDisabled = false,
  approveLabel,
  denyLabel,
  approveTestId = 'inline-approval-approve-button',
  denyTestId = 'inline-approval-deny-button',
  className,
  leading,
}: InlineApprovalActionsProps) => {
  const intl = useIntl();
  const isBusy = disabled || pendingVerdict !== null;
  return (
    <div className={cn('flex items-center justify-end gap-2', className)}>
      {leading}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => {
          void onDeny();
        }}
        disabled={isBusy}
        data-testid={denyTestId}
      >
        {pendingVerdict === 'deny' && <Loader2 className={cn('size-3.5 animate-spin')} />}
        {denyLabel ?? intl.formatMessage(sharedAgentMessages.cancel)}
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          void onApprove();
        }}
        disabled={isBusy || approveDisabled}
        data-testid={approveTestId}
      >
        {pendingVerdict === 'approve' && (
          <Loader2 className={cn('size-3.5 animate-spin')} />
        )}
        {approveLabel ??
          intl.formatMessage({ defaultMessage: 'Approve', id: 'WCaf5CZSTt' })}
      </Button>
    </div>
  );
};
